import type { MovieList } from '@zyx/shared';
import { Plus } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useChangeList, useCreateList, useLists } from '@/hooks/use-library';

/**
 * Names a list, for a new one or a rename. With `addCode`, a new list starts with that
 * movie on it.
 */
export function ListNameDialog(props: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly initial?: string;
  readonly submitLabel: string;
  readonly pending: boolean;
  readonly onSubmit: (name: string) => Promise<unknown>;
}) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-sm">{props.open ? <ListNameForm {...props} /> : null}</DialogContent>
    </Dialog>
  );
}

function ListNameForm(props: Parameters<typeof ListNameDialog>[0]) {
  const [name, setName] = useState(props.initial ?? '');
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void props.onSubmit(name.trim()).then(
      () => props.onOpenChange(false),
      () => undefined,
    );
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <DialogHeader>
        <DialogTitle>{props.title}</DialogTitle>
        <DialogDescription className="sr-only">A name for the list.</DialogDescription>
      </DialogHeader>
      <Input autoFocus value={name} onChange={(event) => setName(event.target.value)} maxLength={100} placeholder="Name" aria-label="List name" />
      <DialogFooter>
        <Button type="submit" disabled={name.trim() === '' || props.pending}>
          {props.submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );
}

/** The new-list dialog, adding `addCode` to the list it makes. */
export function NewListDialog(props: { readonly open: boolean; readonly onOpenChange: (open: boolean) => void; readonly addCode?: string; readonly onCreated?: (list: MovieList) => void }) {
  const create = useCreateList();
  const change = useChangeList();
  return (
    <ListNameDialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={props.addCode === undefined ? 'New list' : `New list with ${props.addCode}`}
      submitLabel="Create"
      pending={create.isPending || change.isPending}
      onSubmit={async (name) => {
        const list = await create.mutateAsync(name);
        // The list exists now whatever happens next; a failed add says so in a toast, and
        // the dialog closes rather than offering to create the same name again.
        if (props.addCode !== undefined) {
          await change.mutateAsync({ listId: list.listId, code: props.addCode, on: true }).catch(() => undefined);
        }
        props.onCreated?.(list);
      }}
    />
  );
}

/** Ticks a movie on or off each custom list, or starts a new list with it. `children` is the trigger. */
export function ListMenu(props: { readonly code: string; readonly listIds: ReadonlyArray<number>; readonly children: ReactNode; readonly align?: 'start' | 'end' }) {
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  // Fetched once the menu opens: a grid of cards should not ask for the lists each.
  const lists = useLists({ enabled: open });
  const change = useChangeList();

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>{props.children}</DropdownMenuTrigger>
        <DropdownMenuContent align={props.align ?? 'end'} className="max-h-80 w-56 overflow-y-auto">
          <DropdownMenuLabel>Lists</DropdownMenuLabel>
          {lists.isPending ? (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">Loading…</p>
          ) : lists.isError ? (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">The lists did not load.</p>
          ) : lists.data.lists.length === 0 ? (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">No lists yet.</p>
          ) : (
            lists.data.lists.map((list) => (
              <DropdownMenuCheckboxItem
                key={list.listId}
                checked={props.listIds.includes(list.listId)}
                disabled={change.isPending}
                // Stays open, so several lists can be ticked in one go.
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={(checked) => change.mutate({ listId: list.listId, code: props.code, on: checked === true })}
              >
                <span className="truncate">{list.name}</span>
              </DropdownMenuCheckboxItem>
            ))
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setNaming(true)}>
            <Plus />
            New list…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <NewListDialog open={naming} onOpenChange={setNaming} addCode={props.code} />
    </>
  );
}
